import { useState, useEffect } from 'react';
import { useApi } from '@shopify/ui-extensions-react/customer-account';
import { getOrderIdFromUrl, formatOrderIdForGraphQL } from '../utils/getOrderIdFromUrl';
import { buildOrderDetailsFromApi } from '../utils/orderDetails';
import { OrderDetails } from '../types';
import { GET_ORDER_DETAILS_QUERY } from '../gql/getOrderDetails.query';
import { logger } from '../utils/logger';

/**
 * @description Hook for the order details
 * @param orderId - The ID of the order
 * @returns An object containing the order details, loading state, and error
 */
export const useOrderDetails = (orderId?: string) => {
  const api = useApi<"customer-account.order-status.block.render">();

  const apiOrderId = api.order?.current?.id;
  const urlOrderId = getOrderIdFromUrl();
  const finalOrderId = orderId || apiOrderId || urlOrderId;

  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiOrderDetails = buildOrderDetailsFromApi(api);

        if (apiOrderDetails) {
          setOrderDetails(apiOrderDetails);
          setLoading(false);
          return;
        }

        const { query } = api;
        const graphqlOrderId = formatOrderIdForGraphQL(finalOrderId || undefined);

        if (graphqlOrderId) {
          logger.debug('Falling back to GraphQL query', {
            graphqlOrderId,
          });

          const result: any = await query(GET_ORDER_DETAILS_QUERY, {
            variables: { id: graphqlOrderId },
          });

          if (result?.data?.order) {
            setOrderDetails(result.data.order);
          } else {
            logger.warn('Order not found in GraphQL result');
            setError('Order not found');
          }
        } else {
          setError('Order ID not available');
        }
      } catch (err: any) {
        logger.error('Error fetching order details', {
          error: err,
          errorMessage: err?.message,
          errorStack: err?.stack,
        });
        setError(err?.message || 'Failed to load order details');
      } finally {
        logger.debug('Fetch completed', {
          loading: false,
          hasOrderDetails: !!orderDetails,
          error: error,
        });
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, finalOrderId, api]);

  useEffect(() => {
    if (orderDetails) {
      console.log('Full Order Data:', orderDetails);
      console.log('Line Items:', orderDetails.lineItems?.edges?.map((edge: any) => edge.node));
    }
  }, [orderDetails]);

  return { orderDetails, loading, error };
};

